// Helper function to get required environment variable
function getRequiredEnvVar(key: string): string {
  const value = import.meta.env[key];
  if (!value) {
    const errorMessage = `Required environment variable ${key} is not set. Please check your .env file.`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
  return value;
}

// Debug logging for auth configuration
// const enableAuth = import.meta.env.VITE_ENABLE_AUTH;
// console.log('[API_CONFIG] VITE_ENABLE_AUTH value:', enableAuth);
// console.log('[API_CONFIG] VITE_ENABLE_AUTH type:', typeof enableAuth);
// console.log('[API_CONFIG] VITE_ENABLE_AUTH === "true":', enableAuth === 'true');
// console.log('[API_CONFIG] All env vars:', import.meta.env);

export const GOOGLE_CONFIG = {
  CLIENT_ID: import.meta.env.PROD
    ? import.meta.env.VITE_GOOGLE_CLIENT_ID_PROD
    : import.meta.env.VITE_GOOGLE_CLIENT_ID_STAGING,
  APP_ID: import.meta.env.PROD
    ? import.meta.env.VITE_GOOGLE_APP_ID_PROD
    : import.meta.env.VITE_GOOGLE_APP_ID_STAGING,
} as const;

export const API_CONFIG = {
  PERMISSIONS_API_URL: getRequiredEnvVar('VITE_PERMISSIONS_API_URL'),
  LLM_RAG_API_URL: getRequiredEnvVar('VITE_LLM_RAG_API_URL'),
  BACKEND_API_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  DATA_INGESTION_API_URL: getRequiredEnvVar('VITE_DATA_INGESTION_URL'),
  ENABLE_AUTH: import.meta.env.VITE_ENABLE_AUTH === 'true',
  ENABLE_STREAMING: import.meta.env.VITE_ENABLE_STREAMING === 'true',
} as const;

//console.log('[API_CONFIG] Final ENABLE_AUTH value:', API_CONFIG.ENABLE_AUTH);

export const API_ENDPOINTS = {
  // User endpoints
  USER: () => `${API_CONFIG.BACKEND_API_URL}/auth/profile/`,
  UPDATE_USER_MODEL: () => `${API_CONFIG.BACKEND_API_URL}/auth/profile/`,
  USERS_LIST: `${API_CONFIG.BACKEND_API_URL}/users/all`,

  // Auth endpoints (OAuth token bridge)
  AUTH: {
    SAVE_OAUTH_TOKEN: `${API_CONFIG.BACKEND_API_URL}/api/auth/save-oauth-token/`,
    OAUTH_CONNECTIONS: `${API_CONFIG.BACKEND_API_URL}/api/auth/oauth-connections/`,
    DISCONNECT_OAUTH: `${API_CONFIG.BACKEND_API_URL}/api/auth/disconnect-oauth/`,
  },

  // Assistant endpoints (Django Backend)
  ASSISTANTS: `${API_CONFIG.BACKEND_API_URL}/assistants/`,
  ASSISTANT_CREATE: `${API_CONFIG.BACKEND_API_URL}/assistants/`,
  ASSISTANT_DETAILS: (assistantId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/assistants/${assistantId}/`,
  ASSISTANT_UPDATE: (assistantId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/assistants/${assistantId}/`,
  ASSISTANT_DELETE: (assistantId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/assistants/${assistantId}/`,
  ASSISTANT_WORKSPACES: `${API_CONFIG.BACKEND_API_URL}/assistants/workspaces/`,

  // Bot endpoints (Legacy - keeping for backward compatibility)
  ALL_BOTS: `${API_CONFIG.BACKEND_API_URL}/bots/all`,
  USER_BOTS: (uid: string) => `${API_CONFIG.BACKEND_API_URL}/bots/user/${uid}`,
  BOT_DETAILS: (botId: string) => `${API_CONFIG.BACKEND_API_URL}/bots/${botId}`,
  BOT_CREATE: `${API_CONFIG.BACKEND_API_URL}/bots/create`,
  BOT_UPDATE: (botId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/bots/${botId}/update`,
  ADD_FAVORITE: (uid: string, botId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/users/${uid}/favorites/add?bot_id=${botId}`,
  REMOVE_FAVORITE: (uid: string, botId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/users/${uid}/favorites/remove?bot_id=${botId}`,

  // Department endpoints (Django Backend)
  DEPARTMENTS: `${API_CONFIG.BACKEND_API_URL}/departments/`,
  DEPARTMENT_DETAILS: (departmentId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/departments/${departmentId}/`,
  DEPARTMENT_ASSISTANTS: (departmentId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/departments/${departmentId}/assistants/`,
  DEPARTMENT_TREE: `${API_CONFIG.BACKEND_API_URL}/departments/tree/`,
  DEPARTMENT_SEARCH: `${API_CONFIG.BACKEND_API_URL}/departments/search/`,

  // AI Models (Django Backend)
  AI_MODELS: `${API_CONFIG.BACKEND_API_URL}/aimodels/`,
  ALL_MODELS: `${API_CONFIG.BACKEND_API_URL}/aimodels/`,

  // User endpoints (Django Backend)
  USERS: `${API_CONFIG.BACKEND_API_URL}/users/`,
  USERS_PROFILE: `${API_CONFIG.BACKEND_API_URL}/auth/profile/`,
  USERS_FAVOURITES: `${API_CONFIG.BACKEND_API_URL}/auth/profile/favourites/`, // GET list - this one is correct
  USERS_RECENTS: `${API_CONFIG.BACKEND_API_URL}/assistants/recents/`,
  TRACK_ASSISTANT: (assistantId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/auth/assistants/${assistantId}/track/`,

  // Conversation tracking endpoints (Django Backend)
  CONVERSATION_CREATE: `${API_CONFIG.BACKEND_API_URL}/conversations/create_conversation/`,
  CONVERSATION_DETAIL: (conversationId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/conversations/${conversationId}/`,
  CONVERSATION_UPDATE: (conversationId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/conversations/${conversationId}/update_conversation/`,
  CONVERSATION_LOCK_MODEL: (conversationId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/conversations/${conversationId}/lock_model/`,
  CONVERSATION_STATS: `${API_CONFIG.BACKEND_API_URL}/conversations/stats/`,

  // Patch Notes endpoints
  PATCH_NOTES: `${API_CONFIG.BACKEND_API_URL}/patch-notes/`,
  PATCH_NOTE_DETAIL: (id: number) =>
    `${API_CONFIG.BACKEND_API_URL}/patch-notes/${id}/`,

  // Assistant Hub endpoints (Legacy - for workspaces)
  WORKSPACES_LIST: `${API_CONFIG.BACKEND_API_URL}/bots/workspaces/list`,
  WORKSPACE_BOTS: (workspaceName: string) =>
    `${API_CONFIG.BACKEND_API_URL}/bots/workspace/${workspaceName}`,

  // Favorites endpoints (Django Backend) - Same endpoint for both add and remove
  FAVORITE_TOGGLE: (assistantId: string) =>
    `${API_CONFIG.BACKEND_API_URL}/auth/profile/favourites/${assistantId}/`,

  // Admin endpoints
  VALIDATE_SUPERADMIN: `${API_CONFIG.BACKEND_API_URL}/superadmin/validate`,
  ADMIN_ANALYTICS: {
    UNIFIED: `${API_CONFIG.BACKEND_API_URL}/api/admin/analytics/unified/`,
  },
  ADMIN_USERS: `${API_CONFIG.BACKEND_API_URL}/admin/users/`,

  // Permission endpoints (Legacy)
  PERMISSIONS: {
    LIST_USERS: (assistantId: string) =>
      `${API_CONFIG.BACKEND_API_URL}/permissions/bot/${assistantId}/users/`,
    ADD: `${API_CONFIG.BACKEND_API_URL}/permissions/add/`,
    MODIFY: `${API_CONFIG.BACKEND_API_URL}/permissions/modify/`,
    REVOKE: `${API_CONFIG.BACKEND_API_URL}/permissions/revoke/`,
  },

  // Chat endpoints
  CONVERSATIONS: `${API_CONFIG.LLM_RAG_API_URL}/conversations`,
  CONVERSATION: (conversationId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/conversations/${conversationId}`,
  DELETE_CONVERSATION: (conversationId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/conversations/${conversationId}`,
  CONVERSATION_UPDATE_TITLE: (conversationId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/conversations/${conversationId}/title`,
  CONVERSATION_RETRY: (conversationId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/conversations/${conversationId}/retry`,

  // Core chat endpoints
  CHAT_INVOKE: `${API_CONFIG.LLM_RAG_API_URL}/chat/invoke`,
  CHAT_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/chat/stream`,
  CHAT_VOICE: `${API_CONFIG.LLM_RAG_API_URL}/chat/voice`,

  // RAG endpoints
  RAG_INVOKE: `${API_CONFIG.LLM_RAG_API_URL}/rag/invoke`,
  RAG_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/rag/stream`,

  // Multimodal endpoints
  CHAT_MULTIMODAL_INVOKE: `${API_CONFIG.LLM_RAG_API_URL}/chat-multimodal/invoke`,
  CHAT_MULTIMODAL_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/chat/stream/multimodal`,

  // Web search endpoints
  CHAT_WEB_SEARCH_INVOKE: `${API_CONFIG.LLM_RAG_API_URL}/chat-web-search/invoke`,
  CHAT_WEB_SEARCH_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/chat-web-search/stream`,

  // Deep research endpoints
  DEEP_RESEARCH_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/deepresearch/stream`,

  // Document endpoints (LLM RAG API - for legacy compatibility)
  DOCUMENTS: {
    LIST: (id: string) => `${API_CONFIG.LLM_RAG_API_URL}/data-store/${id}`,
    DETAIL: (id: string) => `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}`,
    UPLOAD: `${API_CONFIG.BACKEND_API_URL}/api/documents/upload`,
    BULK_UPLOAD: `${API_CONFIG.BACKEND_API_URL}/api/documents/bulk-upload`,
    DELETE: (id: string) => `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}`,
    RESTORE: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/restore`,
    DOWNLOAD: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/download`,
    NEW_VERSION: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/new-version`,
    VERSIONS: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/versions`,
    SEARCH: `${API_CONFIG.BACKEND_API_URL}/api/documents/search`,
    CLOUD_IMPORT: `${API_CONFIG.BACKEND_API_URL}/api/documents/import-cloud-file`,
  },

  // Django Document endpoints (for new document management)
  DJANGO_DOCUMENTS: {
    LIST: `${API_CONFIG.BACKEND_API_URL}/api/documents/`,
    BROWSE: `${API_CONFIG.BACKEND_API_URL}/api/documents/browse/`,
    DETAIL: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/`,
    UPLOAD: `${API_CONFIG.DATA_INGESTION_API_URL}/api/documents/upload/`,
    BULK_UPLOAD: `${API_CONFIG.DATA_INGESTION_API_URL}/api/documents/bulk-upload/`,
    DELETE: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/`,
    HARD_DELETE: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/hard_delete/`,
    RESTORE: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/restore/`,
    DOWNLOAD: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/download/`,
    PREVIEW: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/preview/`,
    SEARCH: `${API_CONFIG.BACKEND_API_URL}/api/documents/search/`,
    CLOUD_IMPORT: `${API_CONFIG.DATA_INGESTION_API_URL}/api/documents/import-cloud-file/`,
    BULK_CLOUD_IMPORT: `${API_CONFIG.DATA_INGESTION_API_URL}/api/documents/import-cloud-items/`,
    NEW_VERSION: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/new-version/`,
    VERSIONS: (id: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/documents/${id}/versions/`,
    // Folder operations
    FOLDERS: {
      LIST: `${API_CONFIG.BACKEND_API_URL}/api/documents/folders/`,
      DETAIL: (folderId: string) =>
        `${API_CONFIG.BACKEND_API_URL}/api/documents/folders/${folderId}/`,
      DELETE: (folderId: string) =>
        `${API_CONFIG.BACKEND_API_URL}/api/documents/folders/${folderId}/delete/`,
      // Query-param based delete for paths with slashes (SharePoint folders)
      DELETE_BY_QUERY: `${API_CONFIG.BACKEND_API_URL}/api/documents/folders-delete/`,
    },
  },

  // Cloud Import Job endpoints (for tracking bulk cloud imports)
  // Note: To cancel a job, use PATCH to STATUS endpoint with { status: 'cancelled' }
  IMPORT_JOBS: {
    LIST: `${API_CONFIG.BACKEND_API_URL}/api/import-jobs/`,
    STATUS: (jobId: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/import-jobs/${jobId}/`,
    FILES: (jobId: string) =>
      `${API_CONFIG.BACKEND_API_URL}/api/import-jobs/${jobId}/files/`,
  },
  // ACC document endpoints
  ACC: {
    LIST: `${API_CONFIG.BACKEND_API_URL}/api/documents/acc/list/`,
    METADATA: `${API_CONFIG.BACKEND_API_URL}/api/documents/acc/metadata/`,
    REGISTER_AND_INGEST: `${API_CONFIG.BACKEND_API_URL}/api/documents/acc/register-and-ingest/`,
  },
  // Web media endpoints
  CHAT_WEB_MEDIA_INVOKE: `${API_CONFIG.LLM_RAG_API_URL}/chat-web-media/invoke`,
  CHAT_WEB_MEDIA_STREAM: `${API_CONFIG.LLM_RAG_API_URL}/chat-web-media/stream`,

  // Data store endpoints
  DATA_STORES: `${API_CONFIG.LLM_RAG_API_URL}/data-stores`,
  DATA_STORE: (storeId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/data-store/${storeId}`,

  // Job status endpoints
  JOB_STATUS: (jobId: string) => `${API_CONFIG.LLM_RAG_API_URL}/job/${jobId}`,

  // LangGraph AI generation endpoints
  GENERATE_ASSISTANT_CONTENT: `${API_CONFIG.LLM_RAG_API_URL}/generate_assistant_content`,

  // Evaluation endpoints
  EVALUATION: `${API_CONFIG.LLM_RAG_API_URL}/evaluation`,
  EVALUATION_GET: (conversationId: string) =>
    `${API_CONFIG.LLM_RAG_API_URL}/evaluation/${conversationId}`,
  EVALUATION_UPDATE: (conversationId: string, messageIndex: number) =>
    `${API_CONFIG.LLM_RAG_API_URL}/evaluation/${conversationId}/${messageIndex}`,
} as const;

export const REQUEST_CONFIG = {
  TIMEOUT: 300000, // 30 seconds
} as const;
